// ===================================================================
// HMGP — Projects
// CRUD for HMGP mitigation projects with BCA, EHP, and tracking
// ===================================================================

import { useEffect, useState, useMemo } from 'react';
import {
  Title, Text, Paper, Group, Stack, Badge, Button, TextInput, Select,
  Textarea, Modal, Tabs, SimpleGrid, ActionIcon, Loader, Center, Table,
} from '../../../components/gab/GabComponents';
import {
  IconPlus, IconEdit, IconTrash, IconSearch, IconRefresh,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { notifications } from '../../../lib/notifications';
import { useHMGPStore } from '../../../store/hmgpStore';
import { usePAGrantsStore } from '../../../store/paGrantsStore';
import { useActivePADisaster as useActiveDisaster } from '../../../hooks/useActiveDisaster';
import { useAuthStore } from '../../../store/authStore';
import {
  HMGP_PROJECT_TYPE_LABELS, HMGP_PROJECT_TYPE_COLORS,
  HMGP_PHASE_LABELS, HMGP_PHASE_COLORS,
  HMGP_STATUS_LABELS,
  type HMGPProjectType, type HMGPPhase, type HMGPProject,
} from '../../../types/pagrants';
import { fmtUSD as fmt, formatCurrency, stripCurrency } from '../../../lib/formatUtils';

const PROJECT_TYPE_OPTIONS = Object.entries(HMGP_PROJECT_TYPE_LABELS).map(([v, l]) => ({ value: v, label: l }));
const PHASE_OPTIONS = Object.entries(HMGP_PHASE_LABELS).map(([v, l]) => ({ value: v, label: l }));
const STATUS_OPTIONS = Object.entries(HMGP_STATUS_LABELS).map(([v, l]) => ({ value: v, label: l }));

export default function HMGPProjects() {
  const navigate = useNavigate();
  const activeDisaster = useActiveDisaster();
  const disasterId = activeDisaster ? String(activeDisaster.Rid) : '';
  const user = useAuthStore(s => s.user);
  const organizations = usePAGrantsStore(s => s.organizations);
  const fetchOrgs = usePAGrantsStore(s => s.fetchOrganizations);

  const {
    projects, loading, fetchProjects,
    createProject, updateProject, deleteProject,
  } = useHMGPStore();

  useEffect(() => { fetchProjects(); fetchOrgs(); }, []);

  // ── Local state ────────────────────────────────────────────
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState<string | null>(null);
  const [filterPhase, setFilterPhase] = useState<string | null>(null);
  const [sortField, setSortField] = useState<string>('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [editTarget, setEditTarget] = useState<HMGPProject | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<HMGPProject | null>(null);

  // Form state
  const [form, setForm] = useState({
    projectTitle: '', projectType: '', organizationId: '',
    problemDescription: '', scopeOfWork: '', estimatedCost: '',
    federalSharePct: '75', projectAddress: '', projectCity: '',
    projectState: '', projectZip: '', notes: '',
    phase: 'subapplication', status: 'draft',
    bcaRatio: '', bcaMethodology: '',
  });

  function toggleSort(field: string) {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  }
  function sortIcon(field: string) {
    if (sortField !== field) return null;
    return sortDir === 'asc' ? ' ↑' : ' ↓';
  }

  const filtered = useMemo(() => {
    let list = disasterId ? projects.filter(p => p.disasterId === disasterId) : [];
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(p =>
        (p.projectTitle || '').toLowerCase().includes(q) ||
        (p.projectNumber || '').toLowerCase().includes(q)
      );
    }
    if (filterType) list = list.filter(p => p.projectType === filterType);
    if (filterPhase) list = list.filter(p => p.phase === filterPhase);
    if (sortField) {
      list = [...list].sort((a, b) => {
        const av = String((a as any)[sortField] ?? '');
        const bv = String((b as any)[sortField] ?? '');
        const num = !isNaN(Number(av)) && !isNaN(Number(bv));
        const cmp = num ? Number(av) - Number(bv) : av.localeCompare(bv);
        return sortDir === 'asc' ? cmp : -cmp;
      });
    }
    return list;
  }, [projects, disasterId, search, filterType, filterPhase, sortField, sortDir]);

  useEffect(() => { setPage(1); }, [filtered.length]);

  const PAGE_SIZE = 25;
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const orgOptions = organizations.map(o => ({ value: String(o.Rid), label: o.orgName || `Org ${o.Rid}` }));

  function openCreate() {
    setEditTarget(null);
    setForm({
      projectTitle: '', projectType: '', organizationId: '',
      problemDescription: '', scopeOfWork: '', estimatedCost: '',
      federalSharePct: '75', projectAddress: '', projectCity: '',
      projectState: '', projectZip: '', notes: '',
      phase: 'subapplication', status: 'draft',
      bcaRatio: '', bcaMethodology: '',
    });
    setShowModal(true);
  }

  function openEdit(p: HMGPProject) {
    setEditTarget(p);
    setForm({
      projectTitle: p.projectTitle,
      projectType: p.projectType,
      organizationId: p.organizationId,
      problemDescription: p.problemDescription,
      scopeOfWork: p.scopeOfWork,
      estimatedCost: p.estimatedCost,
      federalSharePct: p.federalSharePct || '75',
      projectAddress: p.projectAddress,
      projectCity: p.projectCity,
      projectState: p.projectState,
      projectZip: p.projectZip,
      notes: p.notes,
      phase: p.phase || 'subapplication',
      status: p.status || 'draft',
      bcaRatio: p.bcaRatio,
      bcaMethodology: p.bcaMethodology,
    });
    setShowModal(true);
  }

  async function handleSave() {
    if (!form.projectTitle.trim()) {
      notifications.show({ title: 'Required', message: 'Project title is required', color: 'red' });
      return;
    }
    if (editTarget) {
      await updateProject(editTarget.Rid, { ...form });
      notifications.show({ title: 'Updated', message: 'HMGP project updated', color: 'green' });
    } else {
      await createProject({ ...form, disasterId, creatorId: user?.email || '' });
      notifications.show({ title: 'Created', message: 'HMGP project created', color: 'green' });
    }
    setShowModal(false);
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    await deleteProject(deleteTarget.Rid);
    notifications.show({ title: 'Deleted', message: 'HMGP project deleted', color: 'red' });
    setDeleteTarget(null);
  }

  if (!activeDisaster) {
    return (
      <Stack gap="md">
        <Title order={3}>HMGP Projects</Title>
        <Paper p="lg" radius="md" style={{ background: '#f1f3f5', textAlign: 'center' }}>
          <Text size="lg" fw={600} c="dimmed" mb="sm">No Active Disaster</Text>
          <Text size="sm" c="dimmed">Select an incident to view HMGP projects.</Text>
        </Paper>
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <div>
          <Title order={3}>HMGP Projects</Title>
          <Text size="sm" c="dimmed">Hazard mitigation projects — {activeDisaster.disasterName}</Text>
        </div>
        <Group gap="xs">
          <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />} onClick={fetchProjects}>Refresh</Button>
          <Button size="xs" color="green" leftSection={<IconPlus size={14} />} onClick={openCreate}>New Project</Button>
        </Group>
      </Group>

      {/* Filters */}
      <Group gap="xs">
        <TextInput
          placeholder="Search projects..."
          leftSection={<IconSearch size={14} />}
          size="xs" w={250}
          value={search} onChange={e => setSearch(e.target.value)}
        />
        <Select size="xs" placeholder="Type" data={PROJECT_TYPE_OPTIONS} value={filterType} onChange={setFilterType} clearable w={200} />
        <Select size="xs" placeholder="Phase" data={PHASE_OPTIONS} value={filterPhase} onChange={setFilterPhase} clearable w={180} />
        {(search || filterType || filterPhase) && (
          <Button size="xs" variant="subtle" onClick={() => { setSearch(''); setFilterType(null); setFilterPhase(null); }}>Clear</Button>
        )}
      </Group>

      {loading ? <Center h={200}><Loader /></Center> : filtered.length === 0 ? (
        <Paper p="lg" radius="md" style={{ background: '#f1f3f5', textAlign: 'center' }}>
          <Text c="dimmed">No HMGP projects found. Create one to get started.</Text>
        </Paper>
      ) : (
        <Paper withBorder radius="md" style={{ overflow: 'auto' }}>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ cursor: 'pointer' }} onClick={() => toggleSort('projectNumber')}>Project #{sortIcon('projectNumber')}</Table.Th>
                <Table.Th style={{ cursor: 'pointer' }} onClick={() => toggleSort('projectTitle')}>Title{sortIcon('projectTitle')}</Table.Th>
                <Table.Th style={{ cursor: 'pointer' }} onClick={() => toggleSort('projectType')}>Type{sortIcon('projectType')}</Table.Th>
                <Table.Th>Applicant</Table.Th>
                <Table.Th style={{ cursor: 'pointer' }} onClick={() => toggleSort('phase')}>Phase{sortIcon('phase')}</Table.Th>
                <Table.Th style={{ cursor: 'pointer' }} onClick={() => toggleSort('estimatedCost')}>Estimated Cost{sortIcon('estimatedCost')}</Table.Th>
                <Table.Th style={{ cursor: 'pointer' }} onClick={() => toggleSort('bcaRatio')}>BCA Ratio{sortIcon('bcaRatio')}</Table.Th>
                <Table.Th style={{ cursor: 'pointer' }} onClick={() => toggleSort('status')}>Status{sortIcon('status')}</Table.Th>
                <Table.Th w={80}></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {pageRows.map(p => {
                const org = organizations.find(o => String(o.Rid) === p.organizationId);
                const typeColor = HMGP_PROJECT_TYPE_COLORS[p.projectType as HMGPProjectType] || '#6B7280';
                const phaseColor = HMGP_PHASE_COLORS[p.phase as HMGPPhase] || 'gray';
                return (
                  <Table.Tr key={p.Rid} style={{ cursor: 'pointer' }} onClick={() => navigate(`/pagrants/hmgp/projects/${p.Rid}`)}>
                    <Table.Td><Text size="sm" fw={500}>{p.projectNumber || '---'}</Text></Table.Td>
                    <Table.Td><Text size="sm">{p.projectTitle || '---'}</Text></Table.Td>
                    <Table.Td>
                      <Badge size="sm" style={{ backgroundColor: typeColor, color: '#fff' }}>
                        {HMGP_PROJECT_TYPE_LABELS[p.projectType as HMGPProjectType] || p.projectType || '---'}
                      </Badge>
                    </Table.Td>
                    <Table.Td><Text size="sm">{org?.orgName || '---'}</Text></Table.Td>
                    <Table.Td><Badge size="sm" color={phaseColor}>{HMGP_PHASE_LABELS[p.phase as HMGPPhase] || p.phase}</Badge></Table.Td>
                    <Table.Td><Text size="sm">{p.estimatedCost ? fmt(parseFloat(p.estimatedCost) || 0) : '---'}</Text></Table.Td>
                    <Table.Td>
                      {p.bcaRatio ? (
                        <Badge size="sm" color={parseFloat(p.bcaRatio) >= 1 ? 'green' : 'red'}>
                          {parseFloat(p.bcaRatio).toFixed(2)}
                        </Badge>
                      ) : <Text size="sm" c="dimmed">---</Text>}
                    </Table.Td>
                    <Table.Td><Badge size="sm" color="gray">{HMGP_STATUS_LABELS[p.status as keyof typeof HMGP_STATUS_LABELS] || p.status}</Badge></Table.Td>
                    <Table.Td>
                      <Group gap={4} wrap="nowrap">
                        <ActionIcon variant="subtle" size="sm" onClick={(e) => { e.stopPropagation(); openEdit(p); }}>
                          <IconEdit size={14} />
                        </ActionIcon>
                        <ActionIcon variant="subtle" size="sm" color="red" onClick={(e) => { e.stopPropagation(); setDeleteTarget(p); }}>
                          <IconTrash size={14} />
                        </ActionIcon>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Paper>
      )}

      {totalPages > 1 && (
        <Group justify="center" mt="sm" gap="xs">
          <Button size="xs" variant="subtle" disabled={page === 1} onClick={() => setPage(1)}>««</Button>
          <Button size="xs" variant="subtle" disabled={page === 1} onClick={() => setPage(p => Math.max(1, p - 1))}>‹ Prev</Button>
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            const start = Math.max(1, Math.min(page - 2, totalPages - 4));
            const p2 = start + i;
            return p2 <= totalPages ? (
              <Button key={p2} size="xs" variant={p2 === page ? 'filled' : 'subtle'} onClick={() => setPage(p2)}>{p2}</Button>
            ) : null;
          })}
          <Button size="xs" variant="subtle" disabled={page === totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}>Next ›</Button>
          <Button size="xs" variant="subtle" disabled={page === totalPages} onClick={() => setPage(totalPages)}>»»</Button>
          <Text size="xs" c="dimmed" ml="xs">{filtered.length} total</Text>
        </Group>
      )}

      {/* ── Create/Edit Modal ───────────────────────────────── */}
      <Modal opened={showModal} onClose={() => setShowModal(false)} title={editTarget ? 'Edit HMGP Project' : 'New HMGP Project'} size="lg">
        <Tabs defaultValue="basic">
          <Tabs.List mb="md">
            <Tabs.Tab value="basic">Basic Info</Tabs.Tab>
            <Tabs.Tab value="scope">Scope & BCA</Tabs.Tab>
            <Tabs.Tab value="location">Location</Tabs.Tab>
            <Tabs.Tab value="tracking">Tracking</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="basic">
            <Stack gap="sm">
              <TextInput label="Project Title" required value={form.projectTitle} onChange={e => setForm(f => ({ ...f, projectTitle: e.target.value }))} />
              <SimpleGrid cols={2}>
                <Select label="Mitigation Type" data={PROJECT_TYPE_OPTIONS} value={form.projectType} onChange={v => setForm(f => ({ ...f, projectType: v || '' }))} />
                <Select label="Applicant" data={orgOptions} value={form.organizationId} onChange={v => setForm(f => ({ ...f, organizationId: v || '' }))} searchable />
              </SimpleGrid>
              <SimpleGrid cols={2}>
                <TextInput label="Estimated Cost" value={formatCurrency(form.estimatedCost)}
                  onChange={e => setForm(f => ({ ...f, estimatedCost: stripCurrency(e.target.value) }))}
                  styles={{ input: { fontFamily: 'monospace' } }} />
                <TextInput label="Federal Share %" value={form.federalSharePct} onChange={e => setForm(f => ({ ...f, federalSharePct: e.target.value }))} />
              </SimpleGrid>
              <SimpleGrid cols={2}>
                <Select label="Phase" data={PHASE_OPTIONS} value={form.phase} onChange={v => setForm(f => ({ ...f, phase: v || 'subapplication' }))} />
                <Select label="Status" data={STATUS_OPTIONS} value={form.status} onChange={v => setForm(f => ({ ...f, status: v || 'draft' }))} />
              </SimpleGrid>
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="scope">
            <Stack gap="sm">
              <Textarea label="Problem Description" rows={3} value={form.problemDescription} onChange={e => setForm(f => ({ ...f, problemDescription: e.target.value }))} />
              <Textarea label="Scope of Work" rows={3} value={form.scopeOfWork} onChange={e => setForm(f => ({ ...f, scopeOfWork: e.target.value }))} />
              <SimpleGrid cols={2}>
                <TextInput label="BCA Ratio" value={form.bcaRatio} onChange={e => setForm(f => ({ ...f, bcaRatio: e.target.value }))} />
                <Select label="BCA Methodology" data={[
                  { value: 'bca_toolkit', label: 'FEMA BCA Toolkit' },
                  { value: 'pre_calculated', label: 'Pre-Calculated Benefits' },
                  { value: 'streamlined', label: 'Streamlined BCA' },
                ]} value={form.bcaMethodology} onChange={v => setForm(f => ({ ...f, bcaMethodology: v || '' }))} />
              </SimpleGrid>
              <Textarea label="Notes" rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="location">
            <Stack gap="sm">
              <TextInput label="Address" value={form.projectAddress} onChange={e => setForm(f => ({ ...f, projectAddress: e.target.value }))} />
              <SimpleGrid cols={3}>
                <TextInput label="City" value={form.projectCity} onChange={e => setForm(f => ({ ...f, projectCity: e.target.value }))} />
                <TextInput label="State" value={form.projectState} onChange={e => setForm(f => ({ ...f, projectState: e.target.value }))} />
                <TextInput label="ZIP" value={form.projectZip} onChange={e => setForm(f => ({ ...f, projectZip: e.target.value }))} />
              </SimpleGrid>
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="tracking">
            <Stack gap="sm">
              <SimpleGrid cols={2}>
                <Select label="Phase" data={PHASE_OPTIONS} value={form.phase} onChange={v => setForm(f => ({ ...f, phase: v || 'subapplication' }))} />
                <Select label="Status" data={STATUS_OPTIONS} value={form.status} onChange={v => setForm(f => ({ ...f, status: v || 'draft' }))} />
              </SimpleGrid>
            </Stack>
          </Tabs.Panel>
        </Tabs>

        <Group justify="flex-end" mt="lg">
          <Button variant="light" onClick={() => setShowModal(false)}>Cancel</Button>
          <Button color="green" onClick={handleSave}>{editTarget ? 'Save Changes' : 'Create Project'}</Button>
        </Group>
      </Modal>

      {/* ── Delete confirm ──────────────────────────────────── */}
      <Modal opened={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Delete HMGP Project" size="sm">
        <Text size="sm" mb="md">
          Delete project <strong>{deleteTarget?.projectTitle}</strong>? This cannot be undone.
        </Text>
        <Group justify="flex-end">
          <Button variant="light" onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="red" onClick={handleDelete}>Delete</Button>
        </Group>
      </Modal>
    </Stack>
  );
}
